
import React, {useState, useEffect, ReactNode} from 'react';
import {__} from 'js/tool/page/current';
import client from 'js/tool/graphql/client/apollo';
import {getMetadataQuery, MetaDataContext, saveCurrency, setMetaData} from 'js/tool/metaData/helper';
import Alert from 'react-bootstrap/Alert';
import {useCacheAndNetworkQuery} from 'js/admin/components/adAccounts/tool/useCachedHook';
import {MetaDataQuery, MetadataCustomers, MetadataCustomer} from 'js/tool/metaData/types';
import {isFbUsersData} from 'js/tool/metaData/typeGuards';
import Spinner from 'js/reusable/loading';
import {useQuery} from '@apollo/client';
import {IdFilter, customersDataDocument} from 'graphql/generated-fbgraph/graphql';

type IProps = {
	children: ReactNode
}

function getCustomerIds(data?: MetaDataQuery): IdFilter[] {
	if (isFbUsersData(data)) {
		return [{
			value: data?.fbUsers?.[0]?.customers?.map(customer => customer?.customerId ?? '') ?? [],
		}];
	}

	return [];
}

function MetaDataInitializer({children}: IProps) {
	const [customers, setCustomers] = useState<MetadataCustomers>([]);
	const {data, error, refetch} = useCacheAndNetworkQuery<MetaDataQuery>(
		getMetadataQuery(),
		{
			client, onCompleted(data) {
				saveCurrency(data.fbCurrencies);
			},
		}
	);

	const customersQueryObject = useQuery(customersDataDocument,
		{
			client,
			variables: {
				filters: {
					customerId: getCustomerIds(data),
				},
			},
			skip:        !data || !isFbUsersData(data) || (isFbUsersData(data) && !data.fbUsers),
			fetchPolicy: 'cache-and-network',
		}
	);

	useEffect(() => {
		if (customersQueryObject?.data?.customers?.items) {
			setCustomers(customersQueryObject?.data?.customers?.items);
			updateSelectedCustomer();
		}
	}, [customersQueryObject.data]);

	const [selectedCustomer, setSelectedCustomer] = useState<MetadataCustomer | null>(null);

	const [metaDataReady, setMetaDataReady] = useState(false);

	function updateSelectedCustomer() {
		if (selectedCustomer && selectedCustomer.customerId) {
			setSelectedCustomer(customers
				?.find(customer => customer?.customerId === selectedCustomer.customerId) ?? null);
		}
	}

	useEffect(() => {
		setMetaDataReady(setMetaData(data));
	}, [data, error]);

	return (
		<MetaDataContext.Provider
			value={{
				refetch,
				data,
				customers,
				selectedCustomer,
				setSelectedCustomer,
				refetchCustomers:        customersQueryObject.refetch,
				refetchCustomersLoading: customersQueryObject.loading,
			}}
		>
			{
				metaDataReady 
					? children
					: customersQueryObject.loading && (
						<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1}}>
							<Spinner className="fa-3x" />
						</div>
					)
			}
			{
				error &&
				<div style={{padding: 30}}>
					<Alert
						variant="info"
						className="error-alert"
					>
						<i className="fas fa-exclamation-triangle" />
						{__('fb:general error occured msg')}
					</Alert>
				</div>
			}
		</MetaDataContext.Provider>
	);
}

export default MetaDataInitializer;
